import { useState, memo, useMemo, useEffect, FC } from 'react';
import If from 'global/ErrorHandlers/If';
import { trackAutoBuyOrderCanceled } from 'lib/analytics/autoOrder';
import { ORDER_TYPE } from 'constants/trading';
import tradingModalStore from 'global/TradingModal/store';
import useCashInfoStore from 'global/Securities/stores/useCashInfoStore';
import { TransactionFormType } from 'typings/transactionForm';
import useUIConfigStore from 'stores/useUIConfigStore';
import {
  BuyOrderType,
  PaymentMethod,
  TransactionType,
} from 'global/Securities/types/transaction';
import { useFliptBoolean } from 'lib/flipt/hooks';
import { FeatureFlagKeys } from 'lib/flipt/constants/flagKeys';
import tradingDataStore from 'lib/stores/tradingData';
import { EmittenInfoResponse } from 'global/Securities/types/api/emittenInfo';
import { StockType } from 'global/Securities/types/stockType';
import SpecialMonitoringBanner from 'global/TradingModal/components/SpecialMonitoringBanner';
import ModuleFederationForm from 'features/transaction/ModuleFederationForm';
import useTradingStore from 'features/transaction/useTradingStore';
import { useCustomEvent } from 'hooks/useCustomEvent';
import { BuySellEventName, BuySellHideDetail } from 'typings/customEvent';
import {
  clevertapBuyOrderViewed,
  clevertapSellOrderViewed,
} from 'lib/analytics/securities';
import TableOrderbook from '../../TableOrderbook';
import FormHeader from '../../components/Header';
import ExerciseForm from './FormExercise';
import {
  ModalFormWrapper,
  TransactionModalContainer,
  FormWrapper,
  FreezeLayer,
} from '../../styled';
import realTradingStore from '../store';
import { useBondsModalStore } from '../ModalBonds/stores/useBondsModalStore';
import useBondsAnalytics from '../ModalBonds/useBondsAnalytics';
import { BuySellPortal, TransactionModalWrapper } from './styled';
import { MODAL_TYPE } from '../types';

interface BuySellModalProps {
  visible: boolean;
  onUpdateSymbol: (symbol) => void;
  onSubmitExercise: () => void;
  closeModal: () => void;
  onSwitchTab: (prop: string) => void;
}

const transactionToOrderType = {
  [TransactionType.Buy]: ORDER_TYPE.BUY,
  [TransactionType.Sell]: ORDER_TYPE.SELL,
};

const TransactionMFModal: FC<BuySellModalProps> = ({
  visible,
  onUpdateSymbol,
  onSubmitExercise,
  closeModal,
  onSwitchTab,
}) => {
  const isFastOrderEnabled = useFliptBoolean(FeatureFlagKeys.FAST_ORDER);

  const [isSearchActive, setIsSearchActive] = useState(false);
  const resetCashInfo = useCashInfoStore((s) => s.resetCashInfo);
  const { FRBondActionTrackers } = useBondsAnalytics();

  const { showOrderbook, showTabs, orderid, price, setPrice, setToMaxLot } =
    tradingModalStore();

  const orderType = useTradingStore((s) => s.orderType);
  const formType = useTradingStore((s) => s.formType);
  const resetOrderData = realTradingStore((s) => s.resetOrderData);
  const setUIValue = useUIConfigStore((s) => s.setUIValue);
  const openBondsBuyModal = useBondsModalStore((s) => s.openBuyModal);
  const company = tradingDataStore(
    (s) => s.company?.data as EmittenInfoResponse,
  );
  const setModal = useTradingStore((s) => s.setModal);
  const marketHour = company?.market_hour;

  const isExercise = orderType === ORDER_TYPE.EXERCISE;
  const isAmend = useMemo(
    () => [ORDER_TYPE.AMEND_BUY, ORDER_TYPE.AMEND_SELL].includes(orderType),
    [orderType],
  );

  const [isModalHidden, setIsModalHidden] = useState(false);

  const isFormTypeSelectVisible = useMemo(
    () =>
      isFastOrderEnabled &&
      [ORDER_TYPE.BUY, ORDER_TYPE.SELL].includes(orderType) &&
      [
        StockType.Saham,
        StockType.Waran,
        StockType.Right,
        StockType.Etf,
      ].includes(company?.type_company?.toLowerCase() as StockType),
    [isFastOrderEnabled, isAmend],
  );

  const defaultTabValue = useMemo(() => {
    if ([(ORDER_TYPE.BUY, ORDER_TYPE.AMEND_BUY)].includes(orderType)) {
      return TransactionType.Buy;
    }
    if ([ORDER_TYPE.SELL, ORDER_TYPE.AMEND_SELL].includes(orderType)) {
      return TransactionType.Sell;
    }

    return TransactionType.Buy;
  }, [orderType]);

  const handleUpdateSymbol = (selected: string, isBonds?: boolean) => {
    if (isBonds) {
      openBondsBuyModal(selected);
      FRBondActionTrackers.onOrderButtonClicked(
        'Buy',
        selected,
        'Search Company Page',
      );
      closeModal();
      return;
    }
    onUpdateSymbol(selected);
    setIsSearchActive(false);
  };

  const handleClose = () => {
    resetCashInfo();
    closeModal();
    resetOrderData();
    trackAutoBuyOrderCanceled();

    setUIValue('transactionPaymentMethod', PaymentMethod.TradingBalance);
  };

  const handleFormTypeChange = (value: TransactionFormType) => {
    if (value === TransactionFormType.FastOrder) handleClose();
  };

  const handleSwitchTab = (value: TransactionType) => {
    onSwitchTab(transactionToOrderType[value]);
  };

  const showSpecialMonitoringModal = () => {
    setModal(MODAL_TYPE.SPECIAL_MONITORING);
  };

  const symbol = useTradingStore((s) => s.symbol);

  const initialData = {
    orderType,
    orderid,
    currentSymbol: symbol,
    type: formType ?? BuyOrderType.LimitOrder,
    setToMaxLot,
  };

  useCustomEvent<BuySellHideDetail>({
    eventName: BuySellEventName.OrderModalHide,
    onSignal: ({ detail }) => {
      const { isHidden } = detail;
      setIsModalHidden(isHidden);
    },
  });

  useCustomEvent({
    eventName: BuySellEventName.OrderModalClose,
    onSignal: () => {
      handleClose();
    },
  });

  useEffect(() => {
    if (visible) {
      if (orderType === ORDER_TYPE.BUY) {
        clevertapBuyOrderViewed(symbol);
      } else if (orderType === ORDER_TYPE.SELL) {
        clevertapSellOrderViewed(symbol);
      }
    }
  }, [orderType, symbol, visible]);

  return (
    <>
      <BuySellPortal id="buy-sell-portal" />
      <TransactionModalContainer
        width="auto"
        data-cy="transaction-modal-real"
        style={{
          display: isModalHidden ? 'none' : undefined,
        }}
        maskStyle={{
          display: isModalHidden ? 'none' : undefined,
        }}
        visible={visible}
        onCancel={handleClose}
      >
        <TransactionModalWrapper showOrderbook={showOrderbook && !isExercise}>
          <If condition={showOrderbook && !isExercise}>
            <div>
              <TableOrderbook />
            </div>
          </If>
          <ModalFormWrapper>
            {isSearchActive && (
              <FreezeLayer onClick={() => setIsSearchActive(false)} />
            )}
            <FormWrapper>
              <FormHeader
                orderType={orderType}
                defaultTabsValue={defaultTabValue}
                showTabs={showTabs}
                isSearchActive={isSearchActive}
                setIsSearchActive={() => setIsSearchActive(!isSearchActive)}
                formTypeSelectVisible={isFormTypeSelectVisible}
                onUpdateSymbol={handleUpdateSymbol}
                onFormTypeChange={handleFormTypeChange}
                onTabsChange={handleSwitchTab}
                borderless
              />

              <If condition={marketHour?.suspend_info}>
                <SpecialMonitoringBanner
                  onClick={showSpecialMonitoringModal}
                  title={marketHour?.suspend_info}
                />
              </If>
              {isExercise && <ExerciseForm onSubmit={onSubmitExercise} />}
              <If condition={!isExercise}>
                <ModuleFederationForm
                  initialData={initialData}
                  formId="trading_window"
                  customPrice={price}
                  onSwitchTab={() => {
                    if (price) setPrice(null);
                  }}
                />
              </If>
            </FormWrapper>
          </ModalFormWrapper>
        </TransactionModalWrapper>
      </TransactionModalContainer>
    </>
  );
};

export default memo(TransactionMFModal);
